import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useFetch } from "@vueuse/core";
import useMyFetch from "../useMyFetch";
import { useCardsStore } from "@stores/survey/cards";
import { deepCopy } from "@utils/toolBox";
import { getClientId } from "@utils/clientId";
import { captureEvent } from "@/lib/analytics";
import { NotFoundError, ForbiddenError, UnauthorizedError } from "@routes/errors";
import handleError from "@composables/handleError";

export const useSurveyStore = defineStore("survey", () => {
  const survey = ref({});
  const initialSurvey = ref({});
  const sharedDashboardToken = ref(null);
  const isExportingCards = ref(false);

  // getters
  const id = computed(() => survey.value?.id || null);
  const token = computed(() => survey.value?.token || null);

  const hasSurveyChanged = computed(() => {
    const initialSurveyCopy = deepCopy(initialSurvey.value);
    const surveyCopy = deepCopy(survey.value);

    ["errors"].forEach((key) => {
      delete initialSurveyCopy[key];
      delete surveyCopy[key];
    });

    return JSON.stringify(initialSurveyCopy) !== JSON.stringify(surveyCopy);
  });

  const surveyName = computed(() => survey.value?.name ?? "");
  const surveyAccountId = computed(() => survey.value?.account_id);
  const design = computed(() => survey.value?.design);
  const isInitiallyDraft = computed(() => ["draft", "review"].includes(initialSurvey.value?.status));
  const isLiveOrComplete = computed(() => ["live", "complete", "paused"].includes(survey.value?.status));
  const isDraft = computed(() => ["draft", "review"].includes(survey.value.status));
  const isLive = computed(() => ["live", "paused"].includes(survey.value.status));
  const isComplete = computed(() => survey?.value?.status === "complete");
  const areWeightsEnabled = computed(() => survey.value?.enable_weights || false);
  // Accesses the API resolved for *this* survey. A role held on another survey
  // must never grant anything here, so survey pages gate on these — never on
  // the user's global access list.
  const surveyAccesses = computed(() => survey.value?.accesses ?? []);
  const resultsApiPath = computed(() =>
    sharedDashboardToken.value ? `/shared_dashboards/${sharedDashboardToken.value}` : `/surveys/${token.value}`,
  );
  const questionApiPath = (questionId) =>
    sharedDashboardToken.value ? `${resultsApiPath.value}/questions/${questionId}` : `/questions/${questionId}`;
  const canWriteSurvey = computed(() => surveyAccesses.value.includes("surveys.write"));
  const canMonitorSurvey = computed(() => surveyAccesses.value.includes("surveys.monitor"));
  const canAnalyzeSurvey = computed(() => surveyAccesses.value.includes("surveys.analyze"));

  const isAiEnabled = computed(() => survey.value.enable_ai);
  const isSignificanceShown = computed(() => survey.value.show_significance);
  const accountId = computed(() => survey.value.account_id);
  const hasMailingAccess = computed(() => survey.value?.has_mailing_access);
  const isAiChatSurvey = computed(() => survey.value?.ai_chat);
  const hasCodification = computed(() => useCardsStore().cards.some((card) => card.is_recode_auto === true));

  // actions
  async function fetch(token) {
    const { data, response } = await useMyFetch(`surveys/${token}`).json();
    if (response.value.status === 404) throw new NotFoundError("Page not found");
    if (response.value.status === 403) throw new ForbiddenError("User not allowed");
    if (response.value.status === 401) throw new UnauthorizedError("User not logged in");

    // The survey might be cached in sessionStorage
    const surveyInSessionStorage = sessionStorage.getItem("survey");
    if (surveyInSessionStorage) {
      survey.value = JSON.parse(surveyInSessionStorage);
      sessionStorage.removeItem("survey");
    } else {
      survey.value = data.value;
    }

    // DeepCopy breaks the variable's reactivity
    // We'll then have the initial value of the survey
    initialSurvey.value = deepCopy(data.value);

    await useCardsStore().fetch();

    return survey.value;
  }

  async function save({ skipValidation = false, saveAsDraft = false }) {
    const url = `surveys/${token.value}`;
    survey.value.saved_as_draft = saveAsDraft;

    const surveyPayload = survey.value;
    const previousStatus = initialSurvey.value?.status;
    const { data, response } = await useMyFetch(url)
      .patch({ survey: surveyPayload, skip_validation: skipValidation, validate_cards: true, client_id: getClientId() })
      .json();
    if (response.value.ok) {
      survey.value = data.value;
      initialSurvey.value = deepCopy(data.value);

      captureEvent("survey_saved", { saved_as_draft: saveAsDraft });

      if (survey.value.status === "live" && previousStatus !== "live") {
        captureEvent("survey_launched");
      } else if (previousStatus && survey.value.status !== previousStatus) {
        captureEvent("survey_status_changed", {
          from_status: previousStatus,
          to_status: survey.value.status,
        });
      }
    }
    return survey.value;
  }

  async function saveResultsSettings() {
    try {
      const url = `surveys/${token.value}/update_results_settings`;
      const { data, response } = await useMyFetch(url).patch({ survey: survey.value, client_id: getClientId() }).json();
      if (response.value.ok) {
        survey.value.enable_ai = data.value.enable_ai;
        survey.value.show_significance = data.value.show_significance;
        survey.value.charts_config = data.value.charts_config;

        initialSurvey.value.enable_ai = data.value.enable_ai;
        initialSurvey.value.show_significance = data.value.show_significance;
        initialSurvey.value.charts_config = deepCopy(data.value.charts_config);
      }
    } catch (error) {
      handleError(error);
    }
  }

  async function startFromDefault() {
    const { data } = await useMyFetch(`surveys/default`).json();
    const defaultSurvey = {
      ...data.value,
      name: "",
      audience: { ...data.value.audience, other_criteria: "" },
      charts_config: {},
      token: null,
      chat_token: null,
    };

    survey.value = defaultSurvey;

    return survey.value;
  }

  async function create() {
    let formData = new FormData();
    formData.append("survey", JSON.stringify(survey.value));
    formData.append("client_id", getClientId());

    // If the survey is a word import, we need to send the file
    if (survey.value.design?.file != null && survey.value.design?.type === "word") {
      formData.append("file[attachment]", survey.value.design?.file);
      formData.append("file[type]", ".docx");
    }

    const { response, data } = await useMyFetch("surveys").json().post(formData, null);
    if (response.value.ok) {
      survey.value = data.value;
      captureEvent("survey_created", { survey_token: survey.value.token, design_type: survey.value.design?.type });
    }
    return survey.value;
  }

  async function translate(mode, file, locales, translation, cards, specificInstructions = null) {
    let formData = new FormData();
    var localesParam = JSON.stringify(locales);
    formData.append("locales", localesParam);
    formData.append("translation_type", translation);
    formData.append("cards", cards);
    formData.append("mode", mode);
    formData.append("client_id", getClientId());

    if (file != null && mode === "csv") {
      formData.append("file[attachment]", file);
      formData.append("file[type]", ".csv");
    }

    if (specificInstructions) {
      formData.append("specific_instructions", specificInstructions);
    }

    const { response } = await useMyFetch(`surveys/${token.value}/translate`).json().post(formData, null);

    if (response.value.ok) {
      captureEvent("survey_translated", {
        mode,
        translation_type: translation,
        locales_count: locales?.length,
      });
    }
  }

  async function resetCards() {
    let formData = new FormData();
    formData.append("survey_design", JSON.stringify(survey.value.design));
    formData.append("client_id", getClientId());

    // If the survey is a word import, we need to send the file
    if (survey.value.design?.file != null && survey.value.design?.type === "word") {
      formData.append("file[attachment]", survey.value.design?.file);
      formData.append("file[type]", ".docx");
    }

    const { response, data } = await useMyFetch(`surveys/${token.value}/reset`).json().post(formData, null);

    if (response.value.ok) {
      survey.value = data.value;
      captureEvent("survey_cards_reset", { design_type: survey.value.design?.type });
      await useCardsStore().fetch();
    }

    return survey.value;
  }

  async function exportTranslation() {
    try {
      const { response, data } = await useFetch(`/api/surveys/${token.value}/translations/export`).blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(data.value);

      let text = response.value.headers.get("Content-disposition");
      let match = text.match(/filename="([^"]*)"/);
      let filename = match ? match[1] : "undefined";

      link.download = filename;
      link.click();

      captureEvent("survey_translations_exported");
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  }

  async function exportCards(locale) {
    isExportingCards.value = true;
    const { error } = await useMyFetch(`surveys/${token.value}/cards_export`).post(
      { locale, client_id: getClientId() },
      null,
    );
    if (error.value) isExportingCards.value = false;
    else captureEvent("questionnaire_exported", { locale });
  }

  const updateEnableAi = (value) => (survey.value.enable_ai = value);
  const updateShowSignificance = (value) => (survey.value.show_significance = value);

  return {
    // state
    survey,
    initialSurvey,
    sharedDashboardToken,

    // actions
    fetch,
    save,
    saveResultsSettings,
    startFromDefault,
    create,
    translate,
    resetCards,
    exportTranslation,
    exportCards,
    isExportingCards,
    updateEnableAi,
    updateShowSignificance,

    // getters
    id,
    token,
    hasSurveyChanged,
    surveyName,
    surveyAccountId,
    design,
    isInitiallyDraft,
    areWeightsEnabled,
    surveyAccesses,
    resultsApiPath,
    questionApiPath,
    canWriteSurvey,
    canMonitorSurvey,
    canAnalyzeSurvey,
    isComplete,
    isDraft,
    isLive,
    isLiveOrComplete,
    isAiEnabled,
    isSignificanceShown,
    isAiChatSurvey,
    hasCodification,
    accountId,
    hasMailingAccess,
  };
});
